import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Mic, Lock, MessageCircle } from 'lucide-react';
import ProgressButton from './components/ProgressButton';
import FloatingMascot from './components/FloatingMascot';
import BackgroundTetris from './components/BackgroundTetris';

interface NoProgressProps {
  userName?: string;
}

function NoProgress({ userName = 'você' }: NoProgressProps) {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a0a2e] via-[#16082a] to-[#0f0518] relative overflow-x-hidden">
      <BackgroundTetris />

      <div className="progress-inner">

        {/* Header: mesmo layout em web e mobile */}
        <motion.header
          className="progress-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex-shrink-0 pt-1">
            <FloatingMascot size="lg" />
          </div>
          <div>
            <p className="progress-header-greeting">Olá,</p>
            <h1 className="progress-header-name">{userName}</h1>
          </div>
        </motion.header>

        {/* Main Content - centered */}
        <div className="flex-1 flex flex-col justify-center">

          {/* TÍTULO - Hero */}
          <motion.section
            className="text-center mb-10"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.7 }}
          >
            <motion.h1
              className="progress-hero-title mb-3"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.6 }}
            >
              Ainda não tem progresso aqui.
            </motion.h1>
            <motion.p
              className="progress-hero-title text-white uppercase tracking-wide drop-shadow-[0_0_20px_rgba(255,255,255,0.25)]"
              style={{ textShadow: '0 0 24px rgba(255,255,255,0.2)' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.6 }}
            >
              E tá tudo bem.
            </motion.p>

            {/* Loading: progresso sendo construído — blocos no estilo da página */}
            <motion.div
              className="flex justify-center items-end gap-2 mt-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.5 }}
            >
              {[0, 1, 2, 3, 4].map((i) => (
                <motion.div
                  key={i}
                  className="w-3 h-10 rounded-sm bg-white/30 border border-white/50 shadow-[0_0_12px_rgba(255,255,255,0.15)]"
                  animate={{
                    scaleY: [0.4, 1, 0.4],
                    opacity: [0.5, 1, 0.5],
                  }}
                  transition={{
                    duration: 1.2,
                    repeat: Infinity,
                    delay: i * 0.15,
                    ease: 'easeInOut',
                  }}
                  style={{ transformOrigin: 'bottom' }}
                />
              ))}
            </motion.div>
          </motion.section>

          {/* Decorative line */}
          <motion.div
            className="flex justify-center mb-10"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ delay: 0.6, duration: 0.5 }}
          >
            <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-purple-500/50 to-transparent" />
          </motion.div>

          {/* TEXTO DE NORMALIZAÇÃO */}
          <motion.section
            className="text-center mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.6 }}
          >
            <p className="text-white text-lg leading-relaxed mb-4 font-normal" style={{ color: '#fff' }}>
              Muita gente abre o app, explora tudo
              <br />
              e trava antes de falar pela primeira vez.
            </p>
            <motion.p
              className="text-white text-base font-bold"
              style={{ color: '#fff' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
            >
              Isso é normal.
            </motion.p>
          </motion.section>

          {/* Animação: desbloquear progresso falando com o Ataric */}
          <motion.section
            className="mb-10"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.6 }}
          >
            <motion.div
              className="relative flex flex-col items-center justify-center gap-4 rounded-2xl p-6 border border-white/20 bg-white/5 backdrop-blur-sm overflow-hidden"
              animate={{ borderColor: ['rgba(255,255,255,0.2)', 'rgba(255,255,255,0.4)', 'rgba(255,255,255,0.2)'] }}
              transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
            >
              <div className="flex items-center justify-center gap-3">
                <motion.div
                  className="flex items-center justify-center w-10 h-10 rounded-full bg-white/10 border border-white/30"
                  animate={{ scale: [1, 1.05, 1], opacity: [0.8, 1, 0.8] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  <Lock className="w-5 h-5 text-white/80" />
                </motion.div>
                <motion.span
                  className="text-white/60 text-lg"
                  animate={{ opacity: [0.7, 1, 0.7] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  →
                </motion.span>
                <motion.div
                  className="flex items-center justify-center w-10 h-10 rounded-full bg-white/20 border border-white/40"
                  animate={{ scale: [1, 1.15, 1], boxShadow: ['0 0 0 0 rgba(255,255,255,0)', '0 0 20px 4px rgba(255,255,255,0.15)', '0 0 0 0 rgba(255,255,255,0)'] }}
                  transition={{ duration: 1.8, repeat: Infinity }}
                >
                  <MessageCircle className="w-5 h-5 text-white" />
                </motion.div>
              </div>
              <p className="text-center text-white text-base font-medium max-w-[260px]" style={{ color: '#fff' }}>
                Fale com o Ataric para desbloquear seu progresso
              </p>
            </motion.div>
          </motion.section>

          {/* TEXTO DE CONVITE */}
          <motion.section
            className="text-center mb-10"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.6 }}
          >
            <p className="text-white text-lg mb-2 font-bold" style={{ color: '#fff' }}>
              Você não precisa falar muito.
            </p>
            <p className="text-white text-base leading-relaxed font-normal" style={{ color: '#fff' }}>
              Um minuto já é suficiente
              <br />
              <span style={{ color: '#fff' }}>para o seu progresso começar.</span>
            </p>
          </motion.section>

        </div>

        {/* CTA Button */}
        <motion.div
          className="pb-8"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.7 }}
        >
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <ProgressButton
              variant="cta"
              size="xl"
              className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600 border-purple-500/50 shadow-xl shadow-purple-500/30"
              onClick={() => navigate('/')}
            >
              <span className="flex items-center justify-center gap-3">
                <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 1.5, repeat: Infinity }}>
                  <Mic className="w-5 h-5" />
                </motion.div>
                <span className="font-bold tracking-wide">FALAR AGORA</span>
              </span>
            </ProgressButton>
          </motion.div>

          <motion.p
            className="text-center text-white text-xs mt-4 font-bold"
            style={{ color: '#fff' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
          >
            Sem julgamento. Sem nota. Só você falando.
          </motion.p>
        </motion.div>

      </div>
    </div>
  );
}

export default NoProgress;
